<template>
  <div class="delivery-list w-full">
    <!-- En-tête de la tournée avec bouton de synchronisation -->
    <div class="page-header">
      <div class="header-container">
        <!-- Informations de la tournée -->
        <div class="tournee-info">
          <div class="tournee-title">
            <h2 class="page-title">
              <span class="name">{{ tourneeData?.tournee_name }}</span>
              <span class="date">{{ formatDate(tourneeData?.tournee_date) }}</span>
            </h2>
            
            <!-- Progress Bar pour visualiser l'avancement des livraisons -->
            <TourneeProgressBar 
              v-if="tourneeData && (tourneeData.deliveries?.length > 0 || tourneeData.shop_deliveries?.length > 0)"
              :deliveries="tourneeData.deliveries || []"
              :shop-deliveries="tourneeData.shop_deliveries || []"
              class="tournee-progress"
            />
          </div>
        </div>
        
        <!-- Actions d'en-tête dans un conteneur flex -->
        <div class="actions-container">
          <HeaderActions
            :is-online="isOnline"
            :is-user-offline-mode="isUserOfflineMode"
            :is-syncing="isSyncing"
            :has-pending-updates="hasPendingUpdates"
            :has-manual-sync-needed="hasManualSyncNeeded"
            :pending-updates-count="pendingUpdatesCount"
            :sync-tooltip="getSyncTooltip()"
            @refresh="refreshPage"
            @sync="handleSyncClick"
          />
        </div>
      </div>
      
      <!-- Bannières informatives dans un container dédié -->
      <StatusBanners
        :is-user-offline-mode="isUserOfflineMode"
        :is-online="isOnline"
        :has-manual-sync-needed="hasManualSyncNeeded"
        @show-sync-error-details="showSyncErrorDetails"
      />
    </div>

    <ProgressSpinner v-if="loading" class="w-full flex justify-center" />

    <EmptyState
      v-else-if="!tourneeData || combinedDeliveries.length === 0"
      :is-disabled="(!isOnline && !isUserOfflineMode) || isSyncing"
      @action="loadTourneeData"
    />

    <div v-else class="delivery-cards">
      <!-- Carte pour chaque livraison (fusion des deliveries et shop_deliveries) -->
      <template v-for="delivery in combinedDeliveries" :key="delivery.id">
        <!-- Livraison standard -->
        <DeliveryCard
          v-if="!delivery.shop"
          :delivery="delivery"
          :has-pending-updates="hasPendingUpdatesForDelivery(delivery.id)"
          @click="navigateToDelivery(delivery)"
        />
        
        <!-- Livraison shop -->
        <ShopDeliveryCard
          v-else
          :delivery="delivery"
          :has-pending-updates="hasPendingUpdatesForDelivery(delivery.id)"
          @click="navigateToDelivery(delivery)"
        />
      </template>

      <!-- Carte fixe pour le retour au dépôt -->
      <ReturnDepotCard @click="openNavigationToDepot()" />
    </div>
    
    <!-- Bouton flottant pour accéder à la page ambassadeur -->
    <button class="fab-button" @click="navigateToAmbassadeur">
      <i class="pi pi-plus"></i>
    </button>
    
    <!-- Composants cachés pour préchargement des ressources -->
    <div style="display: none;">
      <preload-resources ref="preloadRef"></preload-resources>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, onBeforeUnmount, defineAsyncComponent, watch } from 'vue';
import { useRouter } from 'vue-router';
import ProgressSpinner from 'primevue/progressspinner';

// Importer les composants
import DeliveryCard from './components/DeliveryCard.vue';
import ShopDeliveryCard from './components/ShopDeliveryCard.vue';
import ReturnDepotCard from './components/ReturnDepotCard.vue';
import StatusBanners from './components/StatusBanners.vue';
import HeaderActions from './components/HeaderActions.vue';
import EmptyState from './components/EmptyState.vue';
import TourneeProgressBar from './components/TourneeProgressBar.vue';

// Importation dynamique pour le préchargement
const PreloadResources = defineAsyncComponent({
  loader: () => import('../PreloadResources.vue'),
  delay: 0,
  suspensible: false
});

// Importer les composables
import { useTourneeData } from './composables/useTourneeData';
import { useSynchronization } from './composables/useSynchronization';
import { useNavigation } from './composables/useNavigation';

// Importer les utilitaires
import { formatDate } from './utils/formatter';

// Utiliser les composables
const router = useRouter();
const preloadRef = ref(null);
const storeUnsubscribe = ref(null);

// Utiliser les composables
const {
  loading,
  tourneeData,
  isOnline,
  isUserOfflineMode,
  isSyncing,
  hasPendingUpdates,
  pendingUpdatesCount,
  hasManualSyncNeeded,
  combinedDeliveries,
  loadTourneeData,
  applyPendingStatusUpdates,
  hasPendingUpdatesForDelivery,
  initialize: initializeTourneeData,
  updateShopDeliveryStatus
} = useTourneeData();

const {
  getSyncTooltip,
  handleSyncClick,
  updatePendingCount,
  showSyncErrorDetails
} = useSynchronization();

const {
  navigateToDelivery,
  openNavigationToDepot,
  refreshPage
} = useNavigation();

// Intervalle de rafraîchissement (5 minutes)
const REFRESH_INTERVAL = 5 * 60 * 1000;
const refreshTimer = ref(null);
const lastRefreshTime = ref(Date.now());

// Préchargement des ressources statiques
const preloadStaticResources = () => {
  // Préchargement des icônes PrimeVue couramment utilisées
  const primevueIcons = [
    'check', 'times', 'refresh', 'sync', 'info-circle', 'exclamation-triangle', 
    'map-marker', 'compass', 'wifi', 'wifi-off', 'clock', 'arrow-left', 'phone'
  ];
  
  // Préchargement des images via Service Worker si disponible
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    // Liste des ressources à précharger
    const resourcesToCache = [
      // CSS
      '/css/app.css',
      '/css/chunk-vendors.css',
      
      // Icônes PrimeVue (remplacer par les chemins réels)
      ...primevueIcons.map(icon => `/fonts/primeicons/primeicons.svg#${icon}`)
    ];
    
    // Informer le service worker de précharger ces ressources
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_RESOURCES',
      resources: resourcesToCache
    });
  }
  
  // Préchargement des dépendances CSS
  const preloadCSS = (href) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = href;
    document.head.appendChild(link);
  };
  
  // Styles de l'application
  preloadCSS('/css/app.css');
  preloadCSS('/css/chunk-vendors.css');
};

// Préchargement des composants
const preloadComponents = async () => {
  try {
    if (preloadRef.value) {
      await preloadRef.value.preloadAll();
    }
    
    // Précharger les composants de la page de détail
    const componentsToPreload = [
      await import('../DeliveryDetail/DeliveryInfoCard.vue'),
      await import('../DeliveryDetail/DeliveryAssistanceCard.vue'),
      await import('../Anomalies/AnomalieModal.vue'),
      await import('../Conformes/ConformeModal.vue'),
      await import('../DeliveryFileUpload.vue')
    ];
    
    // Force le compilateur Vue à inclure ces composants dans le build
    componentsToPreload.forEach(component => {
      if (component.default?.__file) {
        // console.log(`Préchargement du composant: ${component.default.__file}`);
      }
    });
    
    // console.log('Préchargement des composants terminé');
  } catch (error) {
    console.error('Erreur lors du préchargement des composants:', error);
  }
};

// Fonction principale pour précharger toutes les ressources
const preloadAllResources = async () => {
  // console.log('Début du préchargement des ressources...');
  
  // Précharger les ressources statiques (CSS, icônes, etc.)
  preloadStaticResources();
  
  // Précharger les composants
  await preloadComponents();
  
  // console.log('Préchargement des ressources terminé');
};

// Fonction pour démarrer le timer de rafraîchissement automatique
const startAutoRefreshTimer = () => {
  // Nettoyage de tout timer existant
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value);
  }
  
  console.log('Démarrage du timer de rafraîchissement automatique (intervalle de 5 minutes)');
  refreshTimer.value = setInterval(async () => {
    console.log('Exécution du rafraîchissement automatique des données');
    // Ne rafraîchir que si en ligne et pas en mode hors ligne explicite
    if (isOnline.value && !isUserOfflineMode.value) {
      // Vérifier si une synchronisation est déjà en cours
      if (!isSyncing.value) {
        lastRefreshTime.value = Date.now();
        await loadTourneeData();
        console.log('Rafraîchissement automatique des données terminé');
      } else {
        console.log('Synchronisation déjà en cours, rafraîchissement automatique reporté');
      }
    } else {
      console.log('Mode hors ligne détecté, rafraîchissement automatique ignoré');
    }
  }, REFRESH_INTERVAL);
};

// Fonction pour arrêter le timer de rafraîchissement
const stopAutoRefreshTimer = () => {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value);
    refreshTimer.value = null;
    console.log('Timer de rafraîchissement automatique arrêté');
  }
};

// Fonction à appeler quand une livraison est mise à jour
const onDeliveryUpdated = async (event) => {
  console.log('MYTOURNEE: Événement delivery-updated reçu', event?.detail);
  
  // Si l'événement contient des détails, essayer de mettre à jour manuellement
  if (event?.detail?.deliveryId && event?.detail?.status) {
    // Vérifier si c'est une shop_delivery
    if (event.detail.shop || event.detail.source === 'shop_delivery') {
      console.log(`Mise à jour manuelle d'une shop_delivery: ${event.detail.deliveryId} -> ${event.detail.status}`);
      // Mettre à jour directement le statut dans les données locales
      updateShopDeliveryStatus(event.detail.deliveryId, event.detail.status);
    }
  }
  
  // Recharger les données après une mise à jour de livraison
  await loadTourneeData();
  
  // Émettre un événement spécifique pour la barre de progression
  window.dispatchEvent(new CustomEvent('tournee-progress-update', {
    detail: {
      deliveries: tourneeData.value?.deliveries || [],
      shopDeliveries: tourneeData.value?.shop_deliveries || [],
      source: 'delivery-updated'
    }
  }));
  
  // Forcer un nouveau rendu pour s'assurer que les changements sont visibles
  setTimeout(() => {
    loading.value = true;
    setTimeout(() => {
      loading.value = false;
    }, 50);
  }, 50);
};

// Fonction pour gérer les mises à jour spécifiques aux shop_deliveries
const onShopDeliveryUpdated = async (event) => {
  console.log('MYTOURNEE: Événement shop-delivery-updated reçu', event?.detail);
  
  // Si l'événement contient des détails, essayer de mettre à jour manuellement
  if (event?.detail?.deliveryId && event?.detail?.status) {
    // Mettre à jour directement le statut dans les données locales
    updateShopDeliveryStatus(event.detail.deliveryId, event.detail.status);
  }
  
  // Recharger les données après une mise à jour de livraison shop
  await loadTourneeData();
  
  // Émettre un événement spécifique pour la barre de progression
  window.dispatchEvent(new CustomEvent('tournee-progress-update', {
    detail: {
      deliveries: tourneeData.value?.deliveries || [],
      shopDeliveries: tourneeData.value?.shop_deliveries || [],
      source: 'shop-delivery-updated'
    }
  }));
  
  // Forcer un nouveau rendu pour s'assurer que les changements sont visibles
  setTimeout(() => {
    loading.value = true;
    setTimeout(() => {
      loading.value = false;
    }, 50);
  }, 50);
};

// Fonction pour gérer les changements de statut spécifiques aux shop_deliveries
const onShopDeliveryStatusChanged = async (event) => {
  console.log('MYTOURNEE: Événement shop-delivery-status-changed reçu', event?.detail);
  
  // Si l'événement contient des détails de la livraison et du statut
  if (event?.detail?.deliveryId && event?.detail?.status) {
    // Mettre à jour directement le statut dans les données locales
    const success = updateShopDeliveryStatus(event.detail.deliveryId, event.detail.status);
    console.log(`Mise à jour manuelle du statut de shop_delivery ${event.detail.deliveryId}: ${success ? 'réussie' : 'échouée'}`);
  }
  
  // Recharger les données pour s'assurer que tous les composants sont à jour
  await loadTourneeData();
  
  // Émettre un événement spécifique pour la barre de progression
  window.dispatchEvent(new CustomEvent('tournee-progress-update', {
    detail: {
      deliveries: tourneeData.value?.deliveries || [],
      shopDeliveries: tourneeData.value?.shop_deliveries || [],
      source: 'shop-delivery-status-changed'
    }
  }));
  
  // Forcer un rendu visuel après le chargement des données
  setTimeout(() => {
    console.log('Rerendu forcé après shop-delivery-status-changed');
    loading.value = true;
    setTimeout(() => {
      loading.value = false;
    }, 50);
  }, 50);
};

// Fonction pour gérer les événements de rafraîchissement forcé
const onForceRefresh = async (event) => {
  console.log(`MYTOURNEE: Événement de rafraîchissement forcé reçu (${event.type})`, event?.detail);
  
  // Si l'événement contient des détails de shop_delivery, mettre à jour explicitement
  if (event?.detail?.source === 'shop_delivery' && event?.detail?.deliveryId && event?.detail?.status) {
    updateShopDeliveryStatus(event.detail.deliveryId, event.detail.status);
  }
  
  // Recharger les données
  await loadTourneeData();
  
  // Émettre un événement spécifique pour la barre de progression
  window.dispatchEvent(new CustomEvent('tournee-progress-update', {
    detail: {
      deliveries: tourneeData.value?.deliveries || [],
      shopDeliveries: tourneeData.value?.shop_deliveries || [],
      source: event.type
    }
  }));
  
  // Forcer un rendu visuel après le chargement des données
  setTimeout(() => {
    console.log('Rerendu forcé après événement de rafraîchissement');
    loading.value = true;
    setTimeout(() => {
      loading.value = false;
    }, 50);
  }, 50);
};

// Fonction pour naviguer vers la page ambassadeur
const navigateToAmbassadeur = () => {
  router.push('/ambassadeur');
};

onMounted(async () => {
  // Enregistrer notre écouteur d'événement au tout début
  console.log('MYTOURNEE: Configuration de l\'écouteur d\'événements delivery-updated');
  // Supprimer d'abord pour éviter les doublons
  window.removeEventListener('delivery-updated', onDeliveryUpdated);
  // Puis ajouter
  window.addEventListener('delivery-updated', onDeliveryUpdated, false);
  
  // Créer un écouteur direct sur le document également (au cas où)
  document.removeEventListener('delivery-updated', onDeliveryUpdated);
  document.addEventListener('delivery-updated', onDeliveryUpdated, false);
  
  // Ajouter l'écouteur pour les shop-delivery-updated
  console.log('MYTOURNEE: Configuration de l\'écouteur d\'événements shop-delivery-updated');
  window.removeEventListener('shop-delivery-updated', onShopDeliveryUpdated);
  window.addEventListener('shop-delivery-updated', onShopDeliveryUpdated, false);
  document.removeEventListener('shop-delivery-updated', onShopDeliveryUpdated);
  document.addEventListener('shop-delivery-updated', onShopDeliveryUpdated, false);
  
  // Ajouter un écouteur pour shop-delivery-status-changed
  console.log('MYTOURNEE: Configuration de l\'écouteur d\'événements shop-delivery-status-changed');
  window.removeEventListener('shop-delivery-status-changed', onShopDeliveryStatusChanged);
  window.addEventListener('shop-delivery-status-changed', onShopDeliveryStatusChanged, false);
  document.removeEventListener('shop-delivery-status-changed', onShopDeliveryStatusChanged);
  document.addEventListener('shop-delivery-status-changed', onShopDeliveryStatusChanged, false);
  
  // Ajouter des écouteurs pour les événements de rafraîchissement
  const refreshEvents = ['mytournee-refresh', 'shop-delivery-refresh', 'force-app-refresh'];
  console.log('MYTOURNEE: Configuration des écouteurs pour les événements de rafraîchissement');
  refreshEvents.forEach(eventName => {
    window.removeEventListener(eventName, onForceRefresh);
    window.addEventListener(eventName, onForceRefresh, false);
    document.removeEventListener(eventName, onForceRefresh);
    document.addEventListener(eventName, onForceRefresh, false);
  });
  
  // Initialiser les données de tournée
  await initializeTourneeData();
  
  // Précharger les ressources après le chargement des données
  await preloadAllResources();

  // Ajouter un écouteur d'événements pour détecter quand l'utilisateur revient à cette page
  const navigationHook = (to, from, next) => {
    // Si l'utilisateur revient à la page de la tournée après avoir consulté une livraison
    if (to.name === 'tournee' && (from.name === 'delivery-detail' || from.name === 'shop-delivery-detail')) {
      console.log('Retour à la page de tournée détecté depuis', from.name);
      
      // Forcer un rechargement complet des données depuis le serveur
      setTimeout(() => {
        // Vider explicitement le cache local pour cette tournée
        if (isOnline.value && !isUserOfflineMode.value) {
          console.log('Forçage du rechargement complet des données depuis le serveur');
          // Simuler un double chargement pour s'assurer de la mise à jour visuelle
          loading.value = true;
          
          // Recharger les données sans utiliser le cache
          store.dispatch('loadTourneeData', { useCache: false, forceRefresh: true }).then(() => {
            // Puis recharger une seconde fois pour s'assurer que les données sont à jour
            setTimeout(() => {
              loadTourneeData().then(() => {
                // Forcer un rendu visuel après le chargement des données
                setTimeout(() => {
                  console.log('Rerendu forcé des composants');
                  loading.value = true;
                  setTimeout(() => {
                    loading.value = false;
                  }, 50);
                }, 50);
              });
            }, 100);
          });
        } else {
          // En mode hors ligne, utiliser le chargement standard
          loadTourneeData();
        }
      }, 100);
    }
    next();
  };
  
  // Ajouter directement l'écouteur
  router.beforeEach(navigationHook);
  
  // Démarrer le timer de rafraîchissement automatique
  startAutoRefreshTimer();
});

// Ajout de onBeforeUnmount pour nettoyer les timers
onBeforeUnmount(() => {
  // Arrêter le timer de rafraîchissement automatique
  stopAutoRefreshTimer();
});

// Amélioration du nettoyage dans onUnmounted
onUnmounted(() => {
  // Clean up the Vuex subscription if it exists
  if (storeUnsubscribe.value) {
    storeUnsubscribe.value();
  }
  
  // Remove the event listeners
  window.removeEventListener('delivery-updated', onDeliveryUpdated);
  document.removeEventListener('delivery-updated', onDeliveryUpdated);
  window.removeEventListener('shop-delivery-updated', onShopDeliveryUpdated);
  document.removeEventListener('shop-delivery-updated', onShopDeliveryUpdated);
  window.removeEventListener('shop-delivery-status-changed', onShopDeliveryStatusChanged);
  document.removeEventListener('shop-delivery-status-changed', onShopDeliveryStatusChanged);
  
  // Supprimer les écouteurs d'événements de rafraîchissement
  const refreshEvents = ['mytournee-refresh', 'shop-delivery-refresh', 'force-app-refresh'];
  refreshEvents.forEach(eventName => {
    window.removeEventListener(eventName, onForceRefresh);
    document.removeEventListener(eventName, onForceRefresh);
  });
  
  // Ensure timers are cleared
  stopAutoRefreshTimer();
});
</script>

<style scoped>
.delivery-list {
  max-width: 100%;
  margin: 0 auto;
}

.page-header {
  position: relative;
  border-bottom: 1px solid #edf2f7;
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;
}

.header-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (min-width: 768px) {
  .header-container {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.tournee-info {
  flex: 1;
  min-width: 0; /* Pour que le truncate fonctionne correctement */
}

.tournee-title {
  padding: 0.5rem 0;
}

.page-title {
  font-weight: 700;
  color: var(--primary-color, #3B82F6);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

@media (min-width: 640px) {
  .page-title {
    flex-direction: row;
    align-items: center;
  }
}

.page-title .name {
  font-size: 1.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-title .date {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

@media (min-width: 640px) {
  .page-title .name::after {
    content: "•";
    margin: 0 0.5rem;
    color: #9ca3af;
  }
}

.tournee-progress {
  margin-top: 0.25rem;
}

.actions-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 0.25rem;
}

@media (min-width: 768px) {
  .actions-container {
    margin-top: 0.5rem;
  }
}

.delivery-cards {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Styles for the floating action button */
.fab-button {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 4.25rem;
  height: 4.25rem;
  border-radius: 50%;
  background-color: var(--primary-color, #3B82F6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  border: none;
  font-size: 2rem;
  font-weight: bold;
  cursor: pointer;
  z-index: 1000;
  transition: transform 0.2s, background-color 0.2s;
}

.fab-button:hover {
  transform: scale(1.05);
  background-color: var(--primary-600, #2563EB);
}

.fab-button:active {
  transform: scale(0.95);
}
</style> 